import { isEqual } from 'lodash'

import { mergePageData, checkSentimentStatus, getNodeCommentablePaths } from '@aytm/app/stats/page/comments/lib/utils'
import { DEFAULT_SEARCH_STATE } from '@aytm/app/stats/page/comments/lib/constants'

export const pageDataReducer = (state = DEFAULT_SEARCH_STATE, action) => {
  switch (action.type) {
    case 'SEARCH_INIT': {
      return {
        ...state,
        errors: [],
        isLoading: true,
        disabled: true
      }
    }
    case 'SET_PARAM': {
      const { name, value } = action.request
      return {
        ...state,
        [name]: value
      }
    }
    case 'SET_PARAMS': {
      const { value } = action.request
      return {
        ...state,
        ...value
      }
    }
    case 'SET_QA_MODE': {
      const { value } = action.request
      return {
        ...state,
        isQAMode: value,
        isQALoading: value ? true : state.isQALoading
      }
    }
    case 'ADD_PROCESSED_QA': {
      const { responseId } = action.request
      return {
        ...state,
        processedQA: state.processedQA.includes(responseId) ? state.processedQA : [...state.processedQA, responseId]
      }
    }
    case 'SET_REQUEST': {
      const { request } = action
      return isEqual(request, state.request)
        ? state
        : {
            ...state,
            request
          }
    }
    case 'SET_REQUEST_PARAM': {
      const { name, value } = action.request
      return {
        ...state,
        request: {
          ...state.request,
          [name]: value
        }
      }
    }
    case 'SET_REQUEST_PARAM_SET': {
      const { request } = action
      // compare the MERGED result: a partial never equals the whole request, so comparing
      // the two directly made every batch a change and re-rendered on no-ops
      const merged = { ...state.request, ...request }
      return isEqual(merged, state.request) ? state : { ...state, request: merged }
    }
    case 'SEARCH_RESULTS': {
      const { data, specialMode = 'comments', pageSize } = action.data
      const { survey, cs, tags, totals, unfilteredTotals } = data

      const isOnePerPage = 1 == pageSize && state.isQAMode
      const pageData = isOnePerPage
        ? mergePageData([...(state.pageData || [])], cs, state.processedQA)
        : [...(cs || [])]

      return {
        ...state,
        pageData: pageData.filter(
          (v, i, a) => v?.response && a.findIndex(_v => _v?.response && _v.response.uid == v.response.uid) == i
        ),
        tagsData: {
          tags,
          tagsStats: survey?.tagsStats || state.tagsData?.tagsStats || {},
          totals
        },
        qaTotal: state.isQAMode
          ? (!state.qaTotal && pageData.length) || (isOnePerPage && totals.responses <= state.qaTotal)
            ? totals.responses
            : state.qaTotal
          : null,
        totalPages:
          'image-responses' == specialMode
            ? Math.ceil((totals && totals.totalImageResponses ? totals.totalImageResponses : 0) / pageSize)
            : Math.ceil((totals && totals.respondents ? totals.respondents : 0) / pageSize),
        totalComments: totals.totalComments || 0,
        totalImageResponses: totals.totalImageResponses || 0,
        totalVideoResponses: totals.totalVideoResponses || 0,
        totalResponses: state.isQAMode ? totals.respondents : totals.responses,
        totalRespondents: totals.totalRespondents,
        totalVerbatims: unfilteredTotals.totalVerbatims || 0,
        totalVerbatimsByLocale: unfilteredTotals.totalVerbatimsByLocale,
        translationsMissing: unfilteredTotals.translationsMissing,
        disabled: false,
        isUpdateQATotal: false
      }
    }
    case 'TAG_MASS_UPDATED': {
      const { tags, tag, newValue } = action.data
      // Clone every touched item + tag rather than mutating in place, so memoized
      // children re-render on the change (see _open-questions.md §OQ-14).
      return {
        ...state,
        pageData: tags
          ? state.pageData.map(item => ({
              ...item,
              tags: (item.tags || []).map(itemTag =>
                Object.hasOwn(tags, itemTag.tag) && tags[itemTag.tag].type == itemTag.type
                  ? { ...itemTag, tag: tags[itemTag.tag].newValue }
                  : itemTag
              )
            }))
          : state.pageData.map(item => ({
              ...item,
              tags: (item.tags || []).map(itemTag =>
                itemTag.tag == tag.tag && itemTag.type == tag.type ? { ...itemTag, tag: newValue } : itemTag
              )
            }))
      }
    }
    case 'TAG_MASS_ADDED': {
      const { tags, tag, path, responseId } = action.data

      const addTag = (itemTags, _tag, _path) => {
        if (itemTags.some(itemTag => itemTag.tag == _tag.tag && itemTag.type == _tag.type && itemTag.path == _path)) {
          return itemTags
        }
        if ('sentiment' == _tag.type) {
          if (itemTags.some(itemTag => 'sentiment' == itemTag.type && itemTag.path == _path)) {
            return itemTags.map(itemTag =>
              'sentiment' == itemTag.type && itemTag.path == _path ? { ...itemTag, tag: _tag.tag } : itemTag
            )
          }
          return checkSentimentStatus(_path, state.sentimentOptions)
            ? [...itemTags, { ..._tag, path: _path }]
            : itemTags
        }
        return [...itemTags, { ..._tag, path: _path }]
      }

      const matches = item =>
        !responseId ||
        '*' == responseId ||
        item.response.uid == responseId ||
        (Array.isArray(responseId) && responseId.includes(item.response.uid))

      const tagList = tags ? Object.keys(tags).map(key => tags[key]) : [tag]

      return {
        ...state,
        pageData: state.pageData.map(item => {
          if (!matches(item)) return item
          let itemTags = item.tags || []
          tagList.forEach(_tag => {
            if (path != undefined && '*' != path) {
              itemTags = addTag(itemTags, _tag, path)
            } else {
              getNodeCommentablePaths(item.answer, _tag.type).forEach(nodePath => {
                itemTags = addTag(itemTags, _tag, nodePath)
              })
            }
          })
          return { ...item, tags: itemTags }
        })
      }
    }
    case 'TAG_MASS_CLONED': {
      const { tags, tag, newValue } = action.data
      return {
        ...state,
        pageData: tags
          ? state.pageData.map(item => ({
              ...item,
              tags: (item.tags || []).reduce((acc, cur) => {
                if (Object.hasOwn(tags, cur.tag) && cur.type == tags[cur.tag].type) {
                  acc.push({ ...cur, tag: tags[cur.tag].newValue, type: 'manual' })
                }
                acc.push(cur)
                return acc
              }, [])
            }))
          : state.pageData.map(item => ({
              ...item,
              tags: (item.tags || []).reduce((acc, cur) => {
                if (cur.tag == tag.tag && cur.type == tag.type) {
                  acc.push({ ...cur, tag: newValue, type: 'manual' })
                }
                acc.push(cur)
                return acc
              }, [])
            }))
      }
    }
    case 'TAG_MASS_MERGED': {
      const { tags, tag, update = false } = action.data
      return {
        ...state,
        pageData: tags
          ? state.pageData.map(item => {
              const itemTags = item.tags || []
              return {
                ...item,
                tags: itemTags.reduce((acc, cur) => {
                  const hasDestinationTag = itemTags.some(
                    itemTag =>
                      itemTag.tag == tags[cur.tag].destination.tag &&
                      itemTag.type == tags[cur.tag].destination.type &&
                      itemTag.path == cur.path
                  )
                  if (Object.hasOwn(tags, cur.tag) && cur.type == tags[cur.tag].type && !hasDestinationTag) {
                    acc.push({ ...cur, tag: tags[cur.tag].destination.tag, type: tags[cur.tag].destination.type })
                  }
                  if (!Object.hasOwn(tags, cur.tag) || !update) acc.push(cur)
                  return acc
                }, [])
              }
            })
          : state.pageData.map(item => {
              const itemTags = item.tags || []
              return {
                ...item,
                tags: itemTags.reduce((acc, cur) => {
                  const hasDestinationTag = itemTags.some(
                    itemTag =>
                      itemTag.tag == tag.destination.tag &&
                      itemTag.type == tag.destination.type &&
                      itemTag.path == cur.path
                  )
                  if (cur.tag == tag.tag && cur.type == tag.type && !hasDestinationTag) {
                    acc.push({ ...cur, tag: tag.destination.tag, type: tag.destination.type })
                  }
                  if (cur.tag != tag.tag || !update) acc.push(cur)
                  return acc
                }, [])
              }
            })
      }
    }
    case 'TAG_MASS_REMOVED': {
      const { tags, tag, path, responseId } = action.data

      const removeTag = (itemTags, _tag, _path) =>
        itemTags
          .map(itemTag => {
            if (
              itemTag.tag == _tag.tag &&
              itemTag.type == _tag.type &&
              (_path == undefined || '*' == _path || itemTag.path == _path)
            ) {
              if ('sentiment' == _tag.type) return { ...itemTag, tag: 'None' }
              return null
            }
            return itemTag
          })
          .filter(itemTag => null !== itemTag)

      const matches = item =>
        !responseId ||
        '*' == responseId ||
        item.response.uid == responseId ||
        (Array.isArray(responseId) && responseId.includes(item.response.uid))

      const tagList = tags ? Object.keys(tags).map(key => tags[key]) : [tag]

      return {
        ...state,
        pageData: state.pageData.map(item => {
          if (!matches(item)) return item
          let itemTags = item.tags || []
          tagList.forEach(_tag => {
            itemTags = removeTag(itemTags, _tag, path)
          })
          return { ...item, tags: itemTags }
        })
      }
    }
    case 'TAG_ADDED': {
      const { tag, responseId, path } = action.data
      return {
        ...state,
        pageData: state.pageData.map(item =>
          item.response.uid == responseId
            ? { ...item, tags: [...(item.tags || []), { ...tag, type: 'manual', path }] }
            : item
        )
      }
    }
    case 'TAG_REMOVED': {
      const { tag, responseId, path } = action.data
      return {
        ...state,
        pageData: state.pageData.map(item =>
          item.response.uid == responseId
            ? {
                ...item,
                tags: item.tags.filter(
                  itemTag =>
                    (itemTag.type == tag.type && itemTag.tag != tag.tag && itemTag.path == path) ||
                    itemTag.path !== path ||
                    itemTag.type !== tag.type
                )
              }
            : item
        )
      }
    }
    case 'SENTIMENT_CHANGED': {
      const { tag, responseId, path } = action.data
      return {
        ...state,
        pageData: state.pageData.map(item => {
          if (item.response.uid != responseId) return item
          const tags = item.tags || []
          const existingTag = tags.find(itemTag => itemTag.type == tag.type && itemTag.path == path)
          return {
            ...item,
            tags: existingTag
              ? tags.map(itemTag => (itemTag === existingTag ? { ...itemTag, ...tag } : itemTag))
              : [...tags, { ...tag, path }]
          }
        })
      }
    }
    case 'FAILURE': {
      const { error } = action
      return {
        ...state,
        isLoading: false,
        disabled: false,
        pageData: [],
        errors: (Array.isArray(error) && error) || [error],
        totalPages: 0,
        totalResponses: 0
      }
    }
    default:
      throw new Error('Unknown action type')
  }
}

export const commentStatusesReducer = (state, action) => {
  const { responseId, scope } = action.data
  switch (action.type) {
    case 'BATCH_DATA': {
      const { batchData, isPreserveState } = action.data
      let ret
      if (isPreserveState) {
        ret = { ...state }
        Object.keys(batchData).forEach(key => {
          ret[key] = {
            ...ret[key],
            ...batchData[key]
          }
        })
      } else {
        ret = {
          ...state,
          ...batchData
        }
      }
      return ret
    }
    case 'SET_STATUS': {
      const { status } = action.data
      return {
        ...state,
        [scope]: {
          ...state[scope],
          [responseId]: status
        }
      }
    }
    case 'CLEANUP': {
      // Clone the nested scope object before deleting, so the prior state's
      // scope is not mutated (see _open-questions.md §OQ-16).
      const scopeState = { ...state[scope] }
      delete scopeState[responseId]
      return { ...state, [scope]: scopeState }
    }
    case 'SET_TAG_STATUS': {
      const { path, status } = action.data
      return {
        ...state,
        [scope]: {
          ...state[scope],
          [responseId]: {
            ...state[scope][responseId],
            [path]: status
          }
        }
      }
    }
    case 'CLEANUP_TAG_STATUS': {
      const { path } = action.data
      if (!state[scope] || !state[scope][responseId]) return state
      const responseState = { ...state[scope][responseId] }
      delete responseState[path]
      return {
        ...state,
        [scope]: { ...state[scope], [responseId]: responseState }
      }
    }
    case 'PRUNE': {
      // Drop status entries for respondents no longer on the page, so the store
      // does not grow unbounded across a long paginating session (see §OQ-10).
      const { uids } = action.data
      const keep = new Set((uids || []).map(String))
      let changed = false
      const next = {}
      for (const scopeKey of Object.keys(state)) {
        const scopeVal = state[scopeKey]
        if (!scopeVal || 'object' != typeof scopeVal) {
          next[scopeKey] = scopeVal
          continue
        }
        next[scopeKey] = Object.keys(scopeVal).reduce((acc, id) => {
          if (keep.has(id)) acc[id] = scopeVal[id]
          else changed = true
          return acc
        }, {})
      }
      return changed ? next : state
    }
    default:
      throw new Error('Unknown action type')
  }
}
